import { useCallback, useEffect, useRef, useState } from "react";
import AssignMenu from "./Components/AssignMenu/AssignMenu";
import ButtonComponent from "./Components/ButtonComponent/ButtonComponent";
import SaveButton from "./Components/ButtonComponent/SaveButton";
import { useDataChanged } from "./Components/Contexts/DataChangedProvider";
import { useData } from "./Components/Contexts/DataProvider";
import { useHistoryPeriod } from "./Components/Contexts/HistoryPeriodProvider";
import { useLoggedInUser } from "./Components/Contexts/LoggedInUserProvider";
import { useRibbon } from "./Components/Contexts/RibbonProvider";
import { useSelectedEntity } from "./Components/Contexts/SelectedEntityProvider";
import { useSelectedRows } from "./Components/Contexts/SelectedRowsProvider";
import { useSelectedTab } from "./Components/Contexts/SelectedTabProvider";
import EntityPicker from "./Components/EntityPicker/EntityPicker";
import GroupBy, { GroupByPopupRef } from "./Components/GroupBy/GroupBy";
import GroupFilter from "./Components/GroupBy/GroupFilter";
import Header from "./Components/Header/Header";
import { useReturn } from "./Components/hooks/useReturn";
import { useSave } from "./Components/hooks/useSave";
import { useSubmit } from "./Components/hooks/useSubmit";
import Loading from "./Components/Loading/Loading";
import RibbonPopup from "./Components/RibbonPopup/RibbonPopup";
import UnsavedChangesPopup from "./Components/SavePopup/UnsavedChangesPopup";
import SubmitPopup from "./Components/SubmitPopup/SubmitPopup";
import BudgetTable from "./Components/Table/BudgetTable";
import FteTable from "./Components/Table/FteTable";
import HistoryBudgetTable from "./Components/Table/HistoryBudgetTable";
import HistoryFteTable from "./Components/Table/HistoryFteTable";
import OriginalFteTable from "./Components/Table/OriginalFteTable";
import OriginalTable from "./Components/Table/OriginalTable";
import SummaryTable from "./Components/Table/SummaryTable";
import {
  budgetPriorityColumns,
  DIGITS_PREFIX,
} from "./Components/Table/TableHelpers";
import TableTabs from "./Components/TableTabs/TableTabs";
import { renameField } from "./General/Helpers";
import "./MainPage.css";
import { SelectedEntity } from "./Models/SelectedEntity";
import { useCsvExport, useLoadData } from "./services/DataService";

const MainPage = () => {
  const { data, originalData } = useData();
  const { loggedInUserId } = useLoggedInUser();
  const { selectedTab, setSelectedTab, preselectedTab } = useSelectedTab();
  const { selectedEntity, setSelectedEntity, preselectedEntity } =
    useSelectedEntity();
  const { setDataChanged } = useDataChanged();
  const { selectedRows } = useSelectedRows();
  const { ribbons } = useRibbon();
  const { setHistoryPeriod } = useHistoryPeriod();

  const [loading, setLoading] = useState(false);

  const [groupByOptions, setGroupByOptions] = useState<any[]>([]);
  const [selectedGroupBys, setSelectedGroupBys] = useState<string[]>([]);

  const [isAssignMenuVisible, setIsAssignMenuVisible] = useState(false);
  const [assignMenuPosition, setAssignMenuPosition] = useState({ x: 0, y: 0 });

  const [isSaveOpen, setIsSaveOpen] = useState(false);
  const [isSubmitPopupOpen, setIsSubmitPopupOpen] = useState(false);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);

  const groupByRef = useRef<GroupByPopupRef>(null);

  const { loadData } = useLoadData();
  const { handleSaveChanges } = useSave();
  const { handleSubmit } = useSubmit();
  const { handleReturn } = useReturn();
  const { handleCsvExport, handleSummaryCsvExport } = useCsvExport();

  useEffect(() => {
    setHistoryPeriod(undefined);
    if (!selectedTab.startsWith("History")) {
      setLoading(true);
      loadData().finally(() => setLoading(false));
    }
  }, [selectedTab, selectedEntity]);

  useEffect(() => {
    if (!data || !Array.isArray(data.value)) return;
    const groupPrefixSet = new Set<string>();
    data.value.forEach((row: any) => {
      Object.keys(row).forEach((key) => {
        const match = renameField(key).match(DIGITS_PREFIX);
        if (match) {
          groupPrefixSet.add(match[1]);
        }
      });
    });
    budgetPriorityColumns.forEach((col) => groupPrefixSet.delete(col));
    const groupOptions = Array.from(groupPrefixSet)
      .sort()
      .map((prefix) => ({
        label: prefix,
        value: prefix,
      }));
    setGroupByOptions(groupOptions);
  }, [data]);

  const handleGroupByClear = () => {
    setSelectedGroupBys([]);
  };

  const handleGroupByFilterDelete = (filterValue: any) => {
    setSelectedGroupBys((prevGroups: any) =>
      prevGroups.filter((f: any) => f !== filterValue)
    );
  };

  const handleGroupByClose = () => groupByRef.current?.closePopup();

  const clearAllPopups = (e: any) => {
    handleGroupByClose();
    setIsAssignMenuVisible(false);
    e.preventDefault();
  };

  const renderTable = useCallback(() => {
    if (selectedTab == "Budget")
      return (
        <BudgetTable
          visibleGroups={selectedGroupBys}
          submitted={selectedEntity?.new_submitted}
        />
      );
    if (selectedTab == "FTE")
      return <FteTable submitted={selectedEntity?.new_submitted} />;
    if (selectedTab == "Sumár - Zjednotená kategória")
      return (
        <SummaryTable
          actualCategoryField="cr864_unifiedcategory"
          originalCategoryField="cr864_unifiedcategory"
          originalTableName="cr864_budgetrecordcopies"
        />
      );
    if (selectedTab == "Sumár - Zložky mzdy")
      return (
        <SummaryTable
          actualCategoryField="cr864_collectioncategoryinbp"
          originalCategoryField="cr864_collectioncategoryinbp"
          originalTableName="cr864_budgetrecordcopies"
        />
      );
    if (selectedTab == "Sumár - ASIS/Legislatíva/Projekt")
      return (
        <SummaryTable
          actualCategoryField="cr864_asisproject"
          originalCategoryField="cr864_asisproject"
          originalTableName="cr864_budgetrecordcopies"
        />
      );
    if (selectedTab == "Summary FTE") {
      return (
        <SummaryTable
          actualCategoryField="new_unifiedcategory"
          originalCategoryField="new_zjednotenakategoria"
          originalTableName="new_originalftes"
        />
      );
    }
    if (selectedTab == "Original Budget")
      return <OriginalTable visibleGroups={selectedGroupBys} />;
    if (selectedTab == "Original FTE") return <OriginalFteTable />;
    if (selectedTab == "History Budget") return <HistoryBudgetTable />;
    if (selectedTab == "History FTE") return <HistoryFteTable />;
    return null;
  }, [data, selectedTab, selectedRows, selectedEntity, selectedGroupBys]);

  const renderRibbons = useCallback(() => {
    return <RibbonPopup />;
  }, [ribbons]);

  return (
    <div className="mainPage" onClick={clearAllPopups}>
      {renderRibbons()}
      <Header />
      <div className="title">
        <EntityPicker setSaveOpen={setIsSaveOpen} />
        <div style={{ height: "30px", marginLeft: "20px" }}>
          {selectedTab == "Budget" &&
            selectedEntity &&
            selectedEntity._ownerid_value == loggedInUserId &&
            !selectedEntity.new_submitted && (
              <ButtonComponent
                name="Submit"
                selected="Submit"
                onClick={() => {
                  setIsSubmitPopupOpen(true);
                  setButtonsDisabled(true);
                  setTimeout(() => {
                    setButtonsDisabled(false);
                  }, 1000);
                }}
                isDisabled={buttonsDisabled}
              />
            )}
        </div>
        <div className="headerButtons">
          {(selectedTab == "Budget" || selectedTab == "FTE") && !loading && (
            <>
              {selectedEntity &&
                selectedEntity._ownerid_value != loggedInUserId &&
                ((selectedTab == "Budget" && data.value.length > 0) ||
                  (selectedTab == "FTE" && data.headCount.length > 0)) && (
                  <ButtonComponent
                    name="Return"
                    selected=""
                    onClick={(e: any) => {
                      handleReturn(selectedEntity._ownerid_value, setLoading);
                      e.stopPropagation();
                      setButtonsDisabled(true);
                      setTimeout(() => {
                        setButtonsDisabled(false);
                      }, 1000);
                    }}
                    isDisabled={buttonsDisabled}
                  />
                )}
              {selectedEntity &&
                selectedEntity._ownerid_value == loggedInUserId &&
                !selectedEntity.new_submitted && (
                  <ButtonComponent
                    name="Assign"
                    selected=""
                    onClick={(e: any) => {
                      setAssignMenuPosition({ x: e.pageX, y: e.pageY });
                      setIsAssignMenuVisible(!isAssignMenuVisible);
                      e.stopPropagation();
                    }}
                  />
                )}
              {selectedEntity && !selectedEntity.new_submitted && (
                <SaveButton
                  name="Save"
                  selected=""
                  onClick={(e: any) => {
                    handleSaveChanges();
                    e.stopPropagation();
                    setButtonsDisabled(true);
                    setTimeout(() => {
                      setButtonsDisabled(false);
                    }, 1000);
                  }}
                  isDisabled={buttonsDisabled}
                />
              )}
            </>
          )}
          {!selectedTab.startsWith("Summary") &&
            data.value.length > 0 &&
            !loading && (
            <ButtonComponent
              name="Export to CSV"
              selected=""
              onClick={() =>
                selectedTab.startsWith("Sumár") || selectedTab == "Summary FTE"
                  ? handleSummaryCsvExport()
                  : handleCsvExport()
              }
            />
          )}
          {isSubmitPopupOpen && (
            <SubmitPopup
              onClose={() => setIsSubmitPopupOpen(false)}
              onSubmit={() => {
                handleSubmit();
                setIsSubmitPopupOpen(false);
              }}
            />
          )}
          {isAssignMenuVisible && (
            <AssignMenu
              x={assignMenuPosition.x}
              y={assignMenuPosition.y}
              onDismiss={setIsAssignMenuVisible}
              setLoading={setLoading}
            />
          )}
          {isSaveOpen && (
            <UnsavedChangesPopup
              onQuit={(e: any) => {
                e.preventDefault();
                if (preselectedEntity) {
                  setSelectedEntity(preselectedEntity as SelectedEntity);
                }
                if (preselectedTab) {
                  setSelectedTab(preselectedTab);
                }
                setDataChanged(false);
                setIsSaveOpen(false);
              }}
              onClose={(e: any) => {
                e.preventDefault();
                setIsSaveOpen(false);
              }}
            />
          )}
        </div>
      </div>
      {loading ? (
        <Loading />
      ) : (
        <div className="contentContainer">
          <TableTabs setSaveOpen={setIsSaveOpen} disabled={loading} />
          <div className="groupByContainer">
            <div style={{ display: "flex", flexDirection: "row", gap: "5px" }}>
              {selectedGroupBys.length > 0 && (
                <GroupFilter
                  filters={selectedGroupBys}
                  onDelete={handleGroupByFilterDelete}
                />
              )}
            </div>
            {(selectedTab === "Budget" ||
              selectedTab === "Original Budget") && (
              <GroupBy
                ref={groupByRef}
                options={groupByOptions}
                selectedGroups={selectedGroupBys}
                onGroupsChange={setSelectedGroupBys}
                onGroupsClear={handleGroupByClear}
              />
            )}
          </div>
          {data && <div className="tableBackground">{renderTable()}</div>}
        </div>
      )}
    </div>
  );
};

export default MainPage;
